/* eslint-disable max-lines */
import { inject, observer } from 'mobx-react';
import { Lambda, observe } from 'mobx';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, ContextStore, ExperimentStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';

import { PackageType } from 'models/multi-item/multi-item-types';

import { UitkButton, UitkTertiaryButton } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLinkAlign } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { SearchForm } from 'components/common/search-form';

const LinkTracked = ClickTracker(Link);
const ButtonTracked = ClickTracker(UitkButton);

export interface PlaybackProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  routeStore?: RouteStore;
  searchResults?: SearchResultsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  bookingNextProperty?: boolean;
  shouldShowMultiItemWizardFields?: boolean;
  packageType: PackageType;
  uiPage?: UIPageStore;
}

export interface PlaybackState {
  jsEnabled: boolean;
  showDatepicker: boolean;
  searchFormExpanded: boolean;
}

@withLocalization
@inject('analytics', 'context', 'experiment', 'routeStore', 'searchResults', 'uiPage')
@observer
export class Playback extends React.Component<PlaybackProps, PlaybackState> {
  private disposerUiPageOpenDatePickerEvent: Lambda;

  constructor(props) {
    super(props);

    this.afterSubmit = this.afterSubmit.bind(this);
    this.handleExpand = this.handleExpand.bind(this);
    this.onUpdateResults = this.onUpdateResults.bind(this);
    this.fetchDatePickerRates = this.fetchDatePickerRates.bind(this);

    const { searchResults, routeStore } = props;

    // Set on client
    // Expand when query has expandForm=true
    searchResults?.setExpandForm(routeStore.queryParams.expandForm);

    this.state = {
      jsEnabled: false,
      showDatepicker: false,
      searchFormExpanded: searchResults?.expandForm,
    };
  }

  public componentDidMount() {
    this.setState({ jsEnabled: true });

    this.disposerUiPageOpenDatePickerEvent = observe(this.props.uiPage, change => {
      if (change.type !== 'remove') {
        const { newValue, name } = change;
        if (name === 'openNewDatePickerEvent' && newValue) {
          this.setState({ searchFormExpanded: true });
        }
      }
    });
  }

  public componentWillUnmount() {
    const { searchResults } = this.props;

    if (searchResults) {
      searchResults.expandForm = false;
    }
    if (this.disposerUiPageOpenDatePickerEvent) {
      this.disposerUiPageOpenDatePickerEvent();
    }
  }

  public componentDidUpdate() {
    const { routeStore } = this.props;
    const updateSearch = routeStore && routeStore.updateSearch && typeof routeStore.updateSearch === 'function';
    const startDate = routeStore && routeStore.queryParams && routeStore.queryParams.startDate;

    if (this.state.showDatepicker && updateSearch && startDate) {
      this.setState({ showDatepicker: false });
      routeStore.updateSearch({ pwaDialog: 'datepicker-start' });
    }
  }

  get searchDateRange() {
    const { l10n, searchResults } = this.props;
    const { searchCriteria } = searchResults;

    if (!searchCriteria || !searchCriteria.startDate || !searchCriteria.endDate) {
      return '';
    }

    const startDate = l10n.formatDateString(searchCriteria.startDate, { skeleton: 'MMMd' });
    const endDate = l10n.formatDateString(searchCriteria.endDate, { skeleton: 'MMMd' });

    return l10n.formatText('hotels.searchResults.playback.dateRange', startDate, endDate);
  }

  private afterSubmit({ packageType, isMobile }) {
    this.setState({ searchFormExpanded: !(isMobile && packageType) });
  }

  private onUpdateResults() {
    this.props.searchResults.clearDefaultRequestFields();
    this.props.searchResults.shouldFetch = true;
  }

  private handleExpand() {
    this.setState(
      {
        searchFormExpanded: !this.state.searchFormExpanded,
      },
      () => {
        this.sendTracking(this.state.searchFormExpanded ? 'ChangeSearch' : 'CloseForm');
      }
    );
  }

  private sendTracking(event) {
    const { analytics } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(`:${event}`, 'Edit search form', null);
    }
  }

  private fetchDatePickerRates() {
    const { searchResults, experiment, context } = this.props;

    return searchResults.fetchRateCalendar(context, experiment);
  }

  public render() {
    const { jsEnabled } = this.state;
    const { searchResults, l10n, bookingNextProperty, shouldShowMultiItemWizardFields, packageType, context } = this.props;
    const { searchCriteria } = searchResults;
    const destination = searchCriteria && searchCriteria.destination;
    const isLoading = searchResults && searchResults.isLoading;
    const searchedDestination = searchResults && searchResults.searchedDestination;
    const destinationMessage = isLoading
      ? destination
      : destination || searchedDestination || <LocalizedText message="hotels.searchResults.playback.destinationMissing.text" />;
    const guestCount = searchCriteria && searchCriteria.guestCount;
    const guestCountMessage = guestCount === 1 ? 'hotels.searchResults.singleGuest' : 'hotels.searchResults.numberOfGuests';
    const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;
    const searchFormProps = {
      onUpdateResults: this.onUpdateResults,
      afterSubmit: this.afterSubmit,
      fetchDatePickerRates: this.fetchDatePickerRates,
      l10n,
      shouldShowMultiItemWizardFields,
      packageType,
      searchResults,
      isMobile,
      bookingNextProperty,
    };
    const searchResultsTitle = l10n.formatText(
      packageType ? `multiItem.searchResults.title.a11yLabel.${PackageType[packageType].toLowerCase()}` : 'hotels.searchResults.title'
    );
    const guestCountTitle = l10n.formatText(guestCountMessage, guestCount);

    const iconAndSubHeading = (
      <UitkLayoutFlex dataTestId="section-hotel-search">
        <UitkLayoutFlexItem alignSelf="center">
          <UitkSpacing margin={{ inlineend: 'two' }}>
            <UitkIcon
              description={l10n.formatText('hotels.searchResults.backToSearchResults.a11yLabel')}
              id="playback-icon"
              name="search"
              size={UitkIconSize.MEDIUM}
              title={searchResultsTitle}
            />
          </UitkSpacing>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <UitkSpacing margin={{ block: 'two', inlinestart: 'one' }}>
            <UitkText align="left" overflow="truncate">
              <UitkText dataTestId="content-location-title" overflow="truncate" size={400} weight="medium">
                {destinationMessage}
              </UitkText>
              <>
                <UitkText dataTestId="playback-date-range" inline={true} size={200} weight="medium">
                  {this.searchDateRange}
                </UitkText>
                {guestCount && (
                  <UitkSpacing padding={{ inline: 'two' }}>
                    <UitkText dataTestId="playback-guest-count" inline={true} size={200} weight="medium" aria-label={`, ${guestCountTitle}`}>
                      {guestCountTitle}
                    </UitkText>
                  </UitkSpacing>
                )}
              </>
            </UitkText>
          </UitkSpacing>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    );

    const playBackLink = (
      <LinkTracked data-stid="playback-action" linkName="Edit search form" rfrr=":ChangeSearch" to="/Hotels">
        <UitkSpacing margin={{ block: 'one' }} padding={{ inlinestart: 'three', inlineend: 'three' }}>
          {iconAndSubHeading}
        </UitkSpacing>
      </LinkTracked>
    );

    const playbackExpander = (
      <UitkTertiaryButton data-stid="form_expander" isFullWidth={true} onClick={this.handleExpand}>
        <UitkText overflow="truncate">{iconAndSubHeading}</UitkText>
      </UitkTertiaryButton>
    );

    const closeForm = (
      <UitkLayoutFlex justifyContent="center">
        <UitkSpacing padding={{ blockend: 'four', blockstart: 'four' }}>
          <ButtonTracked
            linkName="Close search form"
            onClick={this.handleExpand}
            align={UitkLinkAlign.CENTER}
            className="uitk-button-tertiary"
            data-stid="close-search-form"
          >
            <LocalizedText message="hotels.searchResults.close.text" />
          </ButtonTracked>
        </UitkSpacing>
      </UitkLayoutFlex>
    );

    return (
      <Viewport>
        <ViewSmall>
          <div data-stid="section-hotel-search">
            {!this.state.searchFormExpanded && (
              <h2 className="is-visually-hidden">
                <LocalizedText message="hotels.infosite.availabilityWizard.changeSearch.a11yLabel" />
              </h2>
            )}
            {jsEnabled ? (
              <>
                <ChangeSearchForm isHidden={!this.state.searchFormExpanded} {...searchFormProps} />
                {!this.state.searchFormExpanded && playbackExpander}
              </>
            ) : (
              playBackLink
            )}
            {this.state.searchFormExpanded && closeForm}
          </div>
        </ViewSmall>
        <ViewMedium>
          <ChangeSearchForm {...searchFormProps} />
        </ViewMedium>
      </Viewport>
    );
  }
}

export const ChangeSearchForm = ({
  searchResults,
  fetchDatePickerRates,
  onUpdateResults,
  afterSubmit,
  bookingNextProperty = false,
  shouldShowMultiItemWizardFields,
  isMobile,
  packageType,
  l10n,
  isHidden = false,
}) => {
  const sortAndFilter = searchResults && searchResults.sortAndFilter;

  const displayFields: string[] = [];
  if (packageType && packageType !== PackageType.HC) {
    displayFields.push(
      'origin',
      'destination',
      'rooms',
      'cabinClass',
      'dateRange',
      'directFlights',
      'partialStayCheckbox',
      'partialStayDateRange'
    );
  } else {
    displayFields.push('destination', 'dateRange', 'rooms', 'pickUpTime', 'dropOffTime');
  }

  const ignoreFields = ['startDate', 'endDate', 'partialStayStartDate', 'partialStayEndDate'];
  const wrapperClasses = Object.keys(displayFields).map(key => ({
    [key]: 'uitk-cell all-y-gutter-two m-cell-1-1 l-cell-1-3 xl-cell-1-3',
  }));

  const searchFormProps = {
    className: 'uitk-grid l-x-gutter-three all-x-gutter-two all-y-gutter-two',
    callToAction: l10n.formatText('hotels.search.form.submit.label'),
    model: searchResults && searchResults.searchCriteria,
    sortAndFilterOptions: sortAndFilter && sortAndFilter.options,
    sortAndFilterDefaults: sortAndFilter && sortAndFilter.defaults,
    provideDefaultSubmitButton: true,
    autoSubmit: !packageType,
    fetchDatePickerRates,
    ...{
      shouldShowMultiItemWizardFields,
      packageType,
      isMobile,
      afterSubmit,
      ignoreFields,
      displayFields,
      onUpdateResults,
      wrapperClasses,
    },
  };

  return (
    <UitkSpacing margin={'two'} data-stid="section-hotel-search" className={`${isHidden ? 'is-hidden' : ''}`}>
      <div>
        {bookingNextProperty && (
          <UitkSpacing margin={{ blockend: 'two' }}>
            <UitkText size={400} className="pwa-theme--grey-700" weight="bold">
              <LocalizedText message="hotels.searchResults.multiProperty.searchCriteria.header" />
            </UitkText>
          </UitkSpacing>
        )}
        <h2 className="is-visually-hidden">
          <LocalizedText message="hotels.infosite.availabilityWizard.changeSearch.a11yLabel" />
        </h2>
        <SearchForm {...searchFormProps} />
      </div>
    </UitkSpacing>
  );
};
